<template>
  <div class="w-full flex gap-1.5" style="height: 360px">
    <!-- Left Axis Labels -->
    <div v-show="showLeftAxisLabels" class=" relative h-full flex flex-col pb-10 pl-2 pr-1 pt-0.5" style="max-width: 100px">
      <!-- Metric Name -->
      <BaseText type="label" size="xs" class="text-text-muted capitalize mb-2 truncate cursor-default"
      @mouseenter.native="leftAxisLabelHovered = true" @mouseleave.native="leftAxisLabelHovered = false">
        {{ getMetricLookup?.[selectedKpis[0]]?.name || selectedKpis[0] }}
      </BaseText>
      <!-- Axis Labels -->
      <div class="flex-grow flex flex-col justify-between">
        <div v-for="(label, index) in [...leftAxisLabels].reverse()" :key="`left-axis-label-${index}`">
          <BaseText type="body" size="xs" class="text-text-normal">
            {{ getIsPrivateMode ? '••' : label }}
          </BaseText>
        </div>
      </div>
      <!-- Metric Name Tooltip -->
      <transition>
        <div v-if="leftAxisLabelHovered" class="left-metric-name-tooltip px-1.5 py-1 rounded-md bg-neutral-800 bg-opacity-80 shadow-sm">
          <BaseText type="label" size="xs" class="text-white whitespace-nowrap cursor-default">
            {{ getMetricLookup?.[selectedKpis[0]]?.name || selectedKpis[0] }}
          </BaseText>
        </div>
      </transition>
    </div>
    <!-- Bar Graphs -->
    <div v-if="selectedKpis.length" class="flex-grow grid gap-x-1" :style="{ gridTemplateColumns: `repeat(${dataItems.length}, 1fr)` }">
      <BarGraphCard 
        v-for="(item, index) in dataItems" :key="`bar-graph-${item.group_by || item.ad_id}`"
        :data="item" 
        :data-summary="dataSummary"
        :selected-kpis="selectedKpis"
        :axis-scales="axisScales"
        :num-plot-lines="numPlotLines"
        :card-index="index"
        :total-cards="dataItems.length"
        :is-ad-level="isAdLevel"
        :is-public="isPublic"
        @showDetailsDrawer="$emit('showDetailsDrawer', item)"
        @showGroupBreakdown="$emit('showGroupBreakdown', item)"
      />
    </div>
    <!-- Right Axis Labels -->
    <div v-show="showRightAxisLabels" class="relative h-full flex flex-col pb-10 pr-2 pl-1 pt-0.5 text-right" style="max-width: 100px">
      <!-- Metric Name -->
      <BaseText type="label" size="xs" class="text-text-muted capitalize mb-2 truncate cursor-default"
      @mouseenter.native="rightAxisLabelHovered = true" @mouseleave.native="rightAxisLabelHovered = false">
        {{ getMetricLookup?.[selectedKpis[1]]?.name || selectedKpis[1] }}
      </BaseText>
      <!-- Axis Labels -->
      <div class="flex-grow flex flex-col justify-between">
        <div v-for="(label, index) in [...rightAxisLabels].reverse()" :key="`right-axis-label-${index}`">
          <BaseText type="body" size="xs" class="text-text-normal">
            {{ getIsPrivateMode ? '••' : label }}
          </BaseText>
        </div>
      </div>
      <!-- Metric Name Tooltip -->
      <transition>
        <div v-if="rightAxisLabelHovered" class="right-metric-name-tooltip px-1.5 py-1 rounded-md bg-neutral-800 bg-opacity-80 shadow-sm">
          <BaseText type="label" size="xs" class="text-white whitespace-nowrap cursor-default">
            {{ getMetricLookup?.[selectedKpis[1]]?.name || selectedKpis[1] }}
          </BaseText>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { scaleLinear } from 'd3-scale'
import formatLabelString from '../../../../utils/lens/formatGraphLabelString'

import BarGraphCard from './BarGraphCard.vue'

export default {
  name: 'LensBarGraphs',
  components: {
    BarGraphCard
  },
  props: {
    dataItems: {
      type: Array,
      default: () => []
    },
    dataSummary: {
      type: Object,
      default: () => {}
    },
    selectedKpis: {
      type: Array,
      default: () => []
    },
    isAdLevel: {
      type: Boolean,
      default: false
    },
    isPublic: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      axisScales: [],
      leftAxisLabels: [],
      rightAxisLabels: [],
      numPlotLines: 0,
      leftAxisLabelHovered: false,
      rightAxisLabelHovered: false
    }
  },
  watch: {
    dataItems: {
      handler () {
        this.initializeAxisInformation()
      },
      deep: true
    },
    selectedKpis: {
      handler () {
        this.initializeAxisInformation()
      },
      immediate: true,
      deep: true
    }
  },
  computed: {
    ...mapGetters('MetricsModule', ['getMetricLookup']),
    ...mapGetters('MiscModule', ['getIsPrivateMode']),
    showLeftAxisLabels () {
      return this.selectedKpis.length >= 1 && this.selectedKpis.length <= 2
    },
    showRightAxisLabels () {
      return this.selectedKpis.length === 2
    }
  },
  methods: {
    initializeAxisInformation () {
      if (!this.dataItems.length || !this.selectedKpis.length) {
        this.axisScales = []
        this.leftAxisLabels = []
        this.rightAxisLabels = []
        return
      }
      this.computeAxisScales()
      this.computeLeftAxisLabels()
      this.computeRightAxisLabels()
    },
    computeAxisScales () {
      const scales = []
      if (this.dataItems.length) {
        this.selectedKpis.forEach(kpi => {
          scales.push(createKpiAxisScale(this.dataItems.map(item => item[kpi])))
        })
      }
      this.axisScales = scales
    },
    computeLeftAxisLabels () {
      const scale = this.axisScales[0]
      let ticks = scale.ticks(10)
      const reduceTicks = (ticks) => {
        // Ensures that the number of ticks is less than or equal to 10
        let tickValues = [...ticks]
        if (ticks.length % 2 === 0) {
          const newMax = ticks[ticks.length - 1] + ticks[1]
          tickValues.push(newMax)
          scale.domain([0, newMax])
        }
        while (tickValues.length > 10) {
          tickValues = tickValues.filter((_, index) => index % 2 === 0)
        }
        return tickValues
      }
      ticks = reduceTicks(ticks)
      this.leftAxisLabels = ticks.map(tick => this.formatAxisLabelString(tick, 0))
      this.numPlotLines = ticks.length
    },
    computeRightAxisLabels () {
      if (this.selectedKpis.length < 2) {
        this.rightAxisLabels = []
        return
      }
      const numLabels = this.leftAxisLabels.length
      const scale = this.axisScales[1]
      const [_, d1] = scale.domain()
      const maxValue = d1

      const step = maxValue / (numLabels - 1)
      const ticks = Array.from({ length: numLabels }, (_, index) => index * step)
      this.rightAxisLabels = ticks.map(tick => this.formatAxisLabelString(tick, 1))
    },
    formatAxisLabelString (value, selectedKpiIndex) {
      const kpi = this.selectedKpis[selectedKpiIndex]
      const type = this.getMetricLookup?.[kpi]?.type
      return formatLabelString(value, type, this.dataSummary?.currency)
    }
  }
}

const createKpiAxisScale = (values) => {
  let maxValue = Math.max(...values)
  if (maxValue === 0) maxValue = 1
  const scale = scaleLinear()
    .domain([0, maxValue])
    .nice()
  return scale
}
</script>

<style scoped>
.right-metric-name-tooltip {
  position: absolute;
  bottom: calc(100% + 2px);
  right: 4px;
}
.left-metric-name-tooltip {
  position: absolute;
  bottom: calc(100% + 2px);
  left: 4px;
}

.v-enter-active, .v-leave-active {
  transition: opacity 100ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}
</style>